import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { classNames } from "primereact/utils";
import { ReactNode, useMemo, useRef } from "react";
import { UrlObject } from "url";
import { getPathname } from "../../../../../common/utils";

interface NavigationButtonProps {
    className?: string;
    href?: string | UrlObject;
    icon: ReactNode;
    iconClassName?: string;
    label: string;
    menuItems?: (MenuItem & { href?: string | UrlObject })[];
}

const BASE_CLASSNAMES =
    "text-default hover:!text-primary-default !bg-transparent !border-0 !shadow-none hover:text-primary-default";

const ACTIVE_CLASSNAMES =
    "!text-primary-default !bg-transparent !border-0 !shadow-none";

const NavigationButton = (props: NavigationButtonProps) => {
    const { className, href, icon, iconClassName, label, menuItems } = props;

    const { pathname } = useRouter();
    const menuRef = useRef<Menu>(null);

    const isActive = useMemo(() => {
        if (menuItems?.some((item) => getPathname(item.href) === pathname)) {
            return true;
        }

        return getPathname(href) === pathname;
    }, [pathname, href, menuItems]);

    const mappedMenuItems = menuItems?.map((item) => ({
        ...item,
        command: undefined, // ignore when template is used
        template: () => (
            <div className="p-menuitem-content">
                <Link
                    href={item.href as string | UrlObject}
                    className="p-menuitem-link"
                >
                    <span className="p-menuitem-text">{item.label}</span>
                </Link>
            </div>
        ),
    }));

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
        >
            <ButtonWrapper href={!menuItems ? href : undefined}>
                <Button
                    label={label}
                    icon={icon}
                    iconPos="top"
                    className={isActive ? ACTIVE_CLASSNAMES : BASE_CLASSNAMES}
                    onClick={(event) => {
                        if (menuItems) menuRef.current?.toggle(event);
                    }}
                    aria-haspopup={!!menuItems}
                    aria-controls={menuItems ? `${label}-menu` : undefined}
                    pt={{
                        root: {
                            className: classNames(
                                "[&>svg]:mb-2 [&>svg]:h-[2.15rem]",
                                className
                            ),
                        },
                        icon: iconClassName
                            ? { className: iconClassName }
                            : undefined,
                    }}
                />

                {menuItems && (
                    <Menu
                        id={`${label}-menu`}
                        model={mappedMenuItems}
                        popup
                        ref={menuRef}
                    />
                )}
            </ButtonWrapper>
        </motion.div>
    );
};

const ButtonWrapper = ({
    href,
    children,
}: {
    href?: string | UrlObject;
    children: ReactNode;
}) =>
    href ? (
        <Link href={href} className="no-underline">
            {children}
        </Link>
    ) : (
        <>{children}</>
    );

export default NavigationButton;
